import type { DetectedLine, DetectedPolyline } from './detector.js';

/**
 * Four traced edges recognised as the closed rectangle they are.
 *
 * **The measurement.** On the holdout RF09.300.clean the detector returns 13
 * lines. Nine are the drawing's interior rules and are correct. The other four
 * are the edges of the sheet's drawing frame, which ground truth records as ONE
 * closed LWPOLYLINE — and those four are the entire gap between LINE F1 0.818
 * and 1.000 on 22 of the 24 corpus images, at every resolution and in both
 * variants. Nothing else the detector produces is a false positive except one
 * stray rule on RF01 at y 793.7, which is not a frame edge and is not this
 * file's business.
 *
 * **ADR 0009 proposes masking the frame and title block before detecting, and
 * this does not do that.** Masking makes the score come out right by throwing
 * drawing away: the frame is ink on the paper, it belongs in the DXF, and this
 * repository's first promise is that nothing is silently lost. Recognising four
 * edges as one closed rectangle is the same move the vector pipeline already
 * makes when it recognises four Bezier curves as a CIRCLE, it produces exactly
 * the shape ground truth itself records, and the scored outcome is identical —
 * four false positives gone either way, with the difference that the drawing
 * survives.
 *
 * **The failure mode is asymmetric, so the tests are too.** Line recall is
 * 1.000 today. A wrong assembly does not cost one point of precision, it deletes
 * four true lines and adds a false polyline, so every tolerance here is chosen
 * to sit far from the nearest wrong assembly the corpus can offer rather than
 * comfortably around the right one. The nearest wrong assembly is real and
 * measured: two interior rules and the frame's two verticals do form a closed
 * cycle with square corners, and what refuses it is that the verticals run
 * 130.1 pt past one corner and 392.4 pt past the other.
 *
 * Cost is O(P^2) in the number of near-parallel pairs, which the projection
 * overlap test below prunes hard. On the corpus's 13 to 14 detections that is a
 * few hundred candidate cycles and under a millisecond.
 */

/**
 * How far from parallel, and from square, four edges may be.
 *
 * MEASURED over all 24 RF images (`scripts/tmp-rect-measure.ts`), on the frame
 * edges the detector actually produces: opposite sides differ in direction by at
 * most **0.027 degrees** and adjacent sides by at most **0.035 degrees** from
 * square, including on the noisy sheets whose paper is skewed 0.4 degrees — a
 * rotation moves all four edges together, so it does not show up here at all.
 *
 * Two degrees is 57x that worst case, and 4x the detector's own 0.5 degree Hough
 * angle step, so quantisation alone can never reject a true frame. It is still
 * far below anything a quadrilateral that is not a rectangle would show — and
 * the sweep in `scripts/tmp-rect-probe.ts` says this is not the constant that
 * protects the result anyway: every value from 0.5 to 45 degrees assembles the
 * same 23 rectangles across the 24 images and swallows no truth rule. What
 * stands between this and a false positive is the overshoot below.
 */
export const ANGLE_TOLERANCE_DEGREES = 2;

/**
 * How much of a side must actually be inked for a segment to be that side.
 *
 * MEASURED: the traced run covers the full corner-to-corner side on every clean
 * image and on the 300 DPI noisy ones; the worst true frame among the 22 images
 * whose rules still score covers **0.707** of its side (RF01.600.noisy and
 * RF09.600.noisy), because noise breaks a run at `TRACE_GAP_PIXELS` and the
 * trace keeps only the longest fragment.
 *
 * A majority — 0.500 — is the floor. It sits 0.2 below that worst true case and
 * it draws the line in the one place the corpus makes meaningful: RF05.450.noisy
 * traces 0.592 of its shortest side and is assembled, RF05.600.noisy traces
 * 0.472 of its right edge and is refused. Refusing it is deliberate and it is
 * measured: the sweep shows a floor of 0.45 would assemble that 24th frame too,
 * matching truth, swallowing nothing. It is not taken because a floor below half
 * asserts a closed shape from sides that are more absent than present, and the
 * only image it would buy is one whose rules have already fragmented into 14
 * detections and ZERO line true positives — its left edge arrives as two
 * overlapping partial traces. Nothing is lost by refusing: the edges stay in the
 * output as the lines they were traced as.
 */
export const MIN_SIDE_COVERAGE = 0.5;

/**
 * How far a traced edge may run past a corner, **in page points**.
 *
 * This is the test that does the work. Ink continuing past a corner is positive
 * evidence that the corner is not there, which is why it is strict where the
 * coverage floor above is generous: a short trace is only missing evidence,
 * while an overrunning trace is evidence against.
 *
 * MEASURED: a true frame overshoots by 0.48 to 1.18 pt, worst on RF05.450.noisy.
 * The overshoot is physical rather than resolutional — the trace of one edge
 * runs to the far side of the stroke it crosses — so 2 pt is 1.7x the worst true
 * case. The margin on the other side is 65x: `scripts/tmp-rect-nearest.ts` walks
 * the tolerance up a point at a time, and every value from 1.2 pt to **130 pt**
 * assembles the same 23 correct rectangles and nothing else, while **131 pt** is
 * where the corpus first assembles a rectangle truth does not record. The
 * nearest wrong assembly is a real one and it is worth naming: two interior
 * rules and the frame's two verticals, whose verticals run 130.1 pt past one
 * corner and 392.4 pt past the other.
 *
 * In POINTS, converted with `pixelsPerPoint`, because it is a distance on the
 * paper. As a pixel constant it would mean 0.24 pt at 300 DPI and 0.12 pt at
 * 600, which is the same tolerance for two different questions; the corpus spans
 * 2481 to 6751 pixels across, so that error is a factor of nearly three.
 */
export const MAX_OVERSHOOT_POINTS = 2;

/**
 * How far a side adjacent to a **supported** side may stop short of its corners,
 * in page points.
 *
 * A side is *supported* when it is a stretch of a longer traced line rather than
 * a line of its own — which is what the title block's right and bottom edges
 * are, because it sits in the frame's corner and shares them. Overshoot cannot
 * apply to such a side: overrunning is the whole point of it.
 *
 * **Waiving overshoot re-admits assemblies the overshoot test was refusing**, so
 * a supported side buys its exemption with evidence at its ends: every corner at
 * which it meets a whole side must be REACHED by that side. Until now a side
 * could stop as far short as `MIN_SIDE_COVERAGE` allowed, which is 0.5 of it.
 * This is the mirror of the overshoot test and rests on the same claim: ink
 * stopping short of a corner is evidence the corner is not there.
 *
 * **The rule is stated over ownership, not over adjacency, and that correction
 * came from an adversarial review after every gate was green.** Checking only the
 * corners at the ends of a supported side leaves the corner BETWEEN the two whole
 * sides unchecked, where `MIN_SIDE_COVERAGE` allows a side to reach half of it. A
 * single 60 pt tick standing on the same rule was then enough to widen a title
 * block into a box asserting 85 pt of blank paper as its top edge — and to win the
 * evidence sort while doing it, because a supported side is credited with the span
 * of whatever cycle it is in. Every side a rectangle owns must now reach both of
 * its own corners.
 *
 * **Which guard refuses which, stated exactly, because the two are easy to
 * confuse.** The wrong assembly this file names — two interior rules closed by
 * the frame's two verticals — is refused by the *opposite-sides* rule and not by
 * this one: the two verticals are opposite in any rectangle they could bound, and
 * two opposite supported sides leave no corner independently evidenced. This
 * constant decides the other cases, and `rectangles.test.ts` pins them with a
 * negative control that opens the tolerance and watches the same input assemble.
 *
 * MEASURED, and the separation is not close:
 *
 *     the title block's own edges reach their corners       0.0 pt short
 *     the interior rules stop short of the frame by        20.0 pt
 *
 * The rules run x 60 -> 535.276 while the frame's verticals stand at x 40 and
 * 555.276. Two points is a tenth of that, and it matches `MAX_OVERSHOOT_POINTS`
 * because it measures the same physical thing from the other side.
 *
 * **The corpus cannot choose it either, and that is worth being explicit about.**
 * Swept over all 24 images at 0.5, 1, 2, 4, 8, 16, 19 and 20 pt, every score is
 * identical: LINE 213/9/3, CIRCLE 96/0/0, LWPOLYLINE 48/0/0. Nothing on these
 * drawings is decided by it, because the one wrong assembly they offer is refused
 * by the opposite-sides rule before this test is reached. What bounds it is the
 * unit test in `rectangles.test.ts`, which builds the case the corpus does not
 * contain — one supported side with a whole neighbour stopping short — and pins
 * both the refusal and, by opening the tolerance, the fact that this is what
 * refuses it.
 */
export const MAX_CORNER_GAP_POINTS = 2;

export interface RectangleOptions {
  /** From the raster manifest, or from the DPI the page was rendered at. */
  readonly pixelsPerPoint: number;
  readonly angleToleranceDegrees?: number;
  readonly minSideCoverage?: number;
  readonly maxOvershootPoints?: number;
  readonly maxCornerGapPoints?: number;
}

export interface AssembledRectangles {
  /** The rectangles recognised, each consuming exactly four of the input lines. */
  readonly polylines: DetectedPolyline[];
  /** Every line that was not consumed, in the order it arrived. */
  readonly lines: DetectedLine[];
}

interface Vertex {
  readonly x: number;
  readonly y: number;
}

interface Candidate {
  /** Indices into the input, in cycle order: side i runs from corner i to corner i+1. */
  readonly cycle: readonly [number, number, number, number];
  readonly corners: readonly Vertex[];
  readonly confidence: number;
  /** Total traced length, in pixels: how much ink this rectangle is asserted by. */
  readonly evidence: number;
  /**
   * Which sides are stretches of a longer line rather than lines of their own.
   *
   * A supported side does **not** consume its line: the line is shared, which is
   * the entire point. The frame keeps its four edges and the title block in its
   * corner is built from two of them plus two of its own.
   */
  readonly supported: readonly boolean[];
}

/**
 * Recognises closed rectangles among traced lines, and hands back the rest.
 *
 * Pure: the input array and its elements are never touched, and two calls on the
 * same input produce the same output down to the ordering, which is what the
 * acceptance runner's determinism check rests on.
 */
export function assembleRectangles(
  lines: readonly DetectedLine[],
  options: RectangleOptions,
): AssembledRectangles {
  const { pixelsPerPoint } = options;
  if (!Number.isFinite(pixelsPerPoint) || pixelsPerPoint <= 0) {
    throw new Error(
      `pixelsPerPoint is ${String(pixelsPerPoint)}; a page distance cannot be converted with that`,
    );
  }

  const angleTolerance = options.angleToleranceDegrees ?? ANGLE_TOLERANCE_DEGREES;
  const minCoverage = options.minSideCoverage ?? MIN_SIDE_COVERAGE;
  const maxOvershoot = (options.maxOvershootPoints ?? MAX_OVERSHOOT_POINTS) * pixelsPerPoint;
  const maxCornerGap = (options.maxCornerGapPoints ?? MAX_CORNER_GAP_POINTS) * pixelsPerPoint;

  if (lines.length < 4) return { polylines: [], lines: [...lines] };

  const directions = lines.map(direction);

  // Pairs that could be opposite sides. The overlap test is not a heuristic: two
  // sides of the same rectangle project onto the same interval of their shared
  // direction, and each must cover at least half of it, so two that project onto
  // disjoint intervals cannot both be sides of it. Pruning here is what keeps the
  // pair-of-pairs search small on a drawing with many parallel rules.
  const opposite: [number, number][] = [];
  for (let i = 0; i < lines.length; i += 1) {
    for (let j = i + 1; j < lines.length; j += 1) {
      if (angleGap(directions[i]!, directions[j]!) > angleTolerance) continue;
      if (!projectionsOverlap(lines[i]!, lines[j]!, directions[i]!)) continue;
      opposite.push([i, j]);
    }
  }

  const candidates: Candidate[] = [];
  for (let p = 0; p < opposite.length; p += 1) {
    for (let q = p + 1; q < opposite.length; q += 1) {
      const [a, c] = opposite[p]!;
      const [b, d] = opposite[q]!;
      if (a === b || a === d || c === b || c === d) continue;
      const candidate = closeCycle([a, b, c, d], lines, directions, {
        angleTolerance,
        minCoverage,
        maxOvershoot,
        maxCornerGap,
      });
      if (candidate !== null) candidates.push(candidate);
    }
  }

  // Most ink first, so when two candidates want the same edge the better
  // evidenced rectangle takes it. Ties break on the corner coordinates rather
  // than on discovery order, so the output is a property of the geometry.
  candidates.sort(
    (x, y) =>
      y.evidence - x.evidence ||
      x.corners[0]!.y - y.corners[0]!.y ||
      x.corners[0]!.x - y.corners[0]!.x,
  );

  const consumed = new Set<number>();
  const polylines: DetectedPolyline[] = [];
  for (const candidate of candidates) {
    // Only a WHOLE side both claims a line and is blocked by one already claimed.
    // A supported side reads a line it does not own — the frame's edge stays the
    // frame's — so it neither consumes nor is refused by prior consumption.
    if (candidate.cycle.some((index, at) => !candidate.supported[at]! && consumed.has(index))) {
      continue;
    }
    for (const [at, index] of candidate.cycle.entries()) {
      if (!candidate.supported[at]!) consumed.add(index);
    }
    polylines.push({
      kind: 'polyline',
      vertices: candidate.corners,
      closed: true,
      confidence: candidate.confidence,
    });
  }

  return {
    polylines,
    lines: lines.filter((_, index) => !consumed.has(index)),
  };
}

/**
 * Whether these four lines, in this cycle order, close a rectangle.
 *
 * The corners are the INTERSECTIONS of the fitted edge lines, never the traced
 * endpoints, and that is measured rather than tidy: against each image's own
 * ground truth the intersections land 0.518 pt from truth's corners on average
 * and 1.216 pt at worst, while the traced endpoints average 16.94 pt and reach
 * 227.78 pt on RF05.450.noisy. The matching tolerance is 0.92 pt on an A4 sheet,
 * so intersections match truth's frame and endpoints would miss it on six of the
 * 24 images. The reason is not sub-pixel refinement: noise ends a traced run
 * early, which moves an endpoint a third of a side but leaves the line it lies
 * on exactly where it was.
 */
function closeCycle(
  cycle: readonly [number, number, number, number],
  lines: readonly DetectedLine[],
  directions: readonly number[],
  limits: {
    angleTolerance: number;
    minCoverage: number;
    maxOvershoot: number;
    maxCornerGap: number;
  },
): Candidate | null {
  const { angleTolerance, minCoverage, maxOvershoot, maxCornerGap } = limits;
  const sides = cycle.map((index) => lines[index]!);
  const dirs = cycle.map((index) => directions[index]!);

  // Opposite sides parallel and adjacent sides square, stated as the four
  // separate claims they are rather than inferred from the two pairs, so a cycle
  // that is 2 degrees off in each of two places cannot pass as 4 degrees square.
  if (angleGap(dirs[0]!, dirs[2]!) > angleTolerance) return null;
  if (angleGap(dirs[1]!, dirs[3]!) > angleTolerance) return null;
  for (let i = 0; i < 4; i += 1) {
    if (Math.abs(angleGap(dirs[i]!, dirs[(i + 1) % 4]!) - 90) > angleTolerance) return null;
  }

  // Corner i is where side i-1 meets side i, so side i runs corner i to corner i+1.
  const corners: Vertex[] = [];
  for (let i = 0; i < 4; i += 1) {
    const corner = intersect(sides[(i + 3) % 4]!, sides[i]!);
    if (corner === null) return null;
    corners.push(corner);
  }

  // Where each traced side falls along the corner-to-corner span it would be:
  // 0 at the first corner, 1 at the second. Collected before any of it is judged,
  // because whether a side may be `supported` depends on its neighbours.
  const spans: { low: number; high: number; length: number }[] = [];
  for (let i = 0; i < 4; i += 1) {
    const from = corners[i]!;
    const to = corners[(i + 1) % 4]!;
    const sideLength = Math.hypot(to.x - from.x, to.y - from.y);
    if (sideLength === 0) return null;

    const side = sides[i]!;
    const first = parameterAlong(side.x1, side.y1, from, to, sideLength);
    const second = parameterAlong(side.x2, side.y2, from, to, sideLength);
    spans.push({ low: Math.min(first, second), high: Math.max(first, second), length: sideLength });
  }

  // A side is whole on today's terms: enough of it inked, and not running past
  // its corners. Otherwise it may still be SUPPORTED — a stretch of a longer
  // line that covers the side end to end, which is what a shared edge looks like.
  const whole = spans.map(
    (s) => s.high - s.low >= minCoverage && Math.max(-s.low, s.high - 1) * s.length <= maxOvershoot,
  );
  const supported = spans.map((s, i) => !whole[i]! && s.low <= 0 && s.high >= 1);
  for (let i = 0; i < 4; i += 1) if (!whole[i]! && !supported[i]!) return null;

  // Never two opposite: with both, no corner is evidenced by more than one whole
  // side and the shape is asserted from two lines that cross the region rather
  // than bound it.
  if (supported[0]! && supported[2]!) return null;
  if (supported[1]! && supported[3]!) return null;

  // What a supported side buys its exemption with: **every side the rectangle
  // actually owns must reach both of its own corners.**
  //
  // The first version of this checked only the corners at the ends of a supported
  // side, on the reasoning that two adjacent supported sides leave just their
  // shared corner unevidenced. That reasoning is wrong and an adversarial review
  // found it: with supported sides at s and s+1, the checks land on corners s and
  // s+2, the shared corner s+1 is the acknowledged one — and corner s+3, **between
  // the two whole sides**, is never looked at. It kept `MIN_SIDE_COVERAGE`'s
  // laxity, so a side could reach barely half of it.
  //
  // That was harmless while overshoot refused any cycle containing an overrunning
  // line. Exempting a supported side from overshoot makes the whole family
  // admissible, and the evidence sort then prefers the worst of them: a supported
  // side is credited with the span of whatever cycle it is in, so stretching the
  // cycle out to a farther parallel mark ADDS evidence, sorts first, and consumes
  // the sides the true rectangle needed. One 60 pt tick standing on the same rule
  // was enough to replace a title block with a box claiming 85 pt of blank paper.
  //
  // So the rule is stated over ownership rather than over adjacency. A cycle that
  // borrows nothing is unaffected and keeps exactly the behaviour it always had.
  if (supported.some(Boolean)) {
    for (let i = 0; i < 4; i += 1) {
      // A supported side flanked by two more of them is asserted by nothing.
      if (supported[i]! && !whole[(i + 3) % 4]! && !whole[(i + 1) % 4]!) return null;
      if (!whole[i]!) continue;
      const s = spans[i]!;
      if (Math.max(s.low, 0) * s.length > maxCornerGap) return null;
      if (Math.max(1 - s.high, 0) * s.length > maxCornerGap) return null;
    }
  }

  let evidence = 0;
  let confidence = Infinity;
  for (let i = 0; i < 4; i += 1) {
    const s = spans[i]!;
    // Clamped to the side itself, so a supported side is credited with the part
    // of the longer line that lies ON this rectangle and not with its whole
    // length. Without the clamp the title block would out-evidence the frame it
    // borrows from, and be assembled first.
    evidence += (Math.min(s.high, 1) - Math.max(s.low, 0)) * s.length;
    confidence = Math.min(confidence, sides[i]!.confidence);
  }

  return { cycle, corners: canonicalise(corners), confidence, evidence, supported };
}

/**
 * The same four corners, in the traversal ground truth uses.
 *
 * Truth's frame reads (40,40) (555.276,40) (555.276,801.89) (40,801.89): it
 * starts at the topmost-then-leftmost corner and turns clockwise on the page,
 * which in this y-down frame is the positive shoelace direction. Matching it
 * costs nothing and makes a produced polyline diff straight against truth. It is
 * a convenience and not a correctness requirement — `match/candidate.ts` already
 * compares a closed polyline over every equivalent traversal, because winding
 * direction and starting vertex are the producer's choice and not the drawing's.
 */
function canonicalise(corners: readonly Vertex[]): Vertex[] {
  let twiceArea = 0;
  for (let i = 0; i < corners.length; i += 1) {
    const here = corners[i]!;
    const next = corners[(i + 1) % corners.length]!;
    twiceArea += here.x * next.y - next.x * here.y;
  }
  const ordered = twiceArea >= 0 ? [...corners] : [...corners].reverse();

  let start = 0;
  for (let i = 1; i < ordered.length; i += 1) {
    const here = ordered[i]!;
    const best = ordered[start]!;
    if (here.y < best.y || (here.y === best.y && here.x < best.x)) start = i;
  }
  return ordered.map((_, i) => ordered[(start + i) % ordered.length]!);
}

/** Where this point falls along the side, as a fraction: 0 at `from`, 1 at `to`. */
function parameterAlong(x: number, y: number, from: Vertex, to: Vertex, length: number): number {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  return ((x - from.x) * dx + (y - from.y) * dy) / (length * length);
}

/** A segment's direction in degrees, folded into [0, 180): a line has no arrow. */
function direction(line: DetectedLine): number {
  const degrees = (Math.atan2(line.y2 - line.y1, line.x2 - line.x1) * 180) / Math.PI;
  return ((degrees % 180) + 180) % 180;
}

/** The smaller of the two angles between two directions, in [0, 90]. */
function angleGap(a: number, b: number): number {
  const gap = Math.abs(a - b) % 180;
  return Math.min(gap, 180 - gap);
}

function projectionsOverlap(a: DetectedLine, b: DetectedLine, degrees: number): boolean {
  const radians = (degrees * Math.PI) / 180;
  const ux = Math.cos(radians);
  const uy = Math.sin(radians);
  const span = (line: DetectedLine): [number, number] => {
    const first = line.x1 * ux + line.y1 * uy;
    const second = line.x2 * ux + line.y2 * uy;
    return [Math.min(first, second), Math.max(first, second)];
  };
  const [aLow, aHigh] = span(a);
  const [bLow, bHigh] = span(b);
  return Math.min(aHigh, bHigh) >= Math.max(aLow, bLow);
}

/**
 * Where the lines through two segments cross, or null when they do not.
 *
 * The null is unreachable for a cycle that passed the perpendicularity test —
 * the determinant of two segments 90 degrees apart is their lengths' product —
 * and it is here so that a caller loosening `angleToleranceDegrees` towards 90
 * gets no answer rather than a corner at 1e17.
 */
function intersect(a: DetectedLine, b: DetectedLine): Vertex | null {
  const ax = a.x2 - a.x1;
  const ay = a.y2 - a.y1;
  const bx = b.x2 - b.x1;
  const by = b.y2 - b.y1;
  const determinant = ax * by - ay * bx;
  if (determinant === 0) return null;
  const t = ((b.x1 - a.x1) * by - (b.y1 - a.y1) * bx) / determinant;
  return { x: a.x1 + ax * t, y: a.y1 + ay * t };
}
